// 4.4 Vitrine: hero light (WebGL), variable-weight proximity headline, pointer spotlight + tilt, glass header on scroll,
// transform-only scroll reveals. Everything degrades to the static 4.3 look without JS, WebGL or motion.
(() => {
  'use strict';
  const body = document.body;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const band = document.querySelector('[data-vt-band]');
  const ptr = { x: innerWidth * .7, y: innerHeight * .45, nx: .7, ny: .45, at: 0 };
  const wakers = [];
  addEventListener('pointermove', e => {
    ptr.x = e.clientX; ptr.y = e.clientY; ptr.nx = e.clientX / innerWidth; ptr.ny = e.clientY / innerHeight; ptr.at = performance.now();
    wakers.forEach(w => w());
  }, { passive: true });
  // A loop that runs while the pointer moved in the last 1.2 s, then sleeps until the next move.
  const idleLoop = step => {
    let running = false;
    const tick = now => { step(); if (now - ptr.at < 1200) requestAnimationFrame(tick); else running = false; };
    wakers.push(() => { if (!running) { running = true; requestAnimationFrame(tick); } });
  };

  // Header: clear over the hero, glass after it; the band slides under the header by its real height.
  const header = document.querySelector('.pr-header');
  if (band && header) {
    body.classList.add('vt-home');
    const setH = () => body.style.setProperty('--vt-hdr', `${header.offsetHeight}px`);
    setH(); addEventListener('resize', setH);
    const onScroll = () => body.classList.toggle('vt-scrolled', scrollY > 40);
    onScroll(); addEventListener('scroll', onScroll, { passive: true });
  }

  // Headline: split into characters (screen readers keep the label) for the rise-in and the proximity effect.
  const h1 = band && band.querySelector('[data-vt-split]');
  const chars = [];
  if (h1 && !h1.dataset.vtDone) {
    h1.dataset.vtDone = '1';
    const text = h1.textContent.trim();
    h1.setAttribute('aria-label', text);
    let i = 0;
    const words = text.split(/\s+/);
    const lines = [];
    // two lines: the question, then the answer ("Achou no vídeo?" / "Tá aqui.")
    const q = words.findIndex(w => /\?$/.test(w));
    if (q > -1 && q < words.length - 1) lines.push(words.slice(0, q + 1), words.slice(q + 1)); else lines.push(words);
    h1.innerHTML = lines.map(ws => `<span class="vt-line" aria-hidden="true">${ws.map(w => `<span class="vt-word">${[...w].map(c => `<span class="vt-ch" style="--i:${i++}">${c.replace(/[<&>]/g, '')}</span>`).join('')}</span>`).join(' ')}</span>`).join('');
    h1.querySelectorAll('.vt-ch').forEach(c => chars.push(c));
    // the lines clip only for the rise-in; once the last letter lands the clip goes, so the neon glow isn't cut square
    const risen = () => h1.classList.add('vt-risen');
    if (chars.length) chars[chars.length - 1].addEventListener('animationend', risen, { once: true });
    setTimeout(risen, 2600);
  }

  // Pointer spotlight + tilt on tiles, cards, focus band and promises.
  const spots = document.querySelectorAll('[data-vt-spot], .product-card .product-media, .pr-trust a');
  spots.forEach(el => {
    const tiltTarget = el.querySelector('[data-vt-tilt], img');
    // the rect is read once per hover (re-read after a scroll) and the styles written at most once per frame,
    // so moving over a card never forces a layout
    let r = null, ry = -1, ex = 0, ey = 0, queued = false;
    const paint = () => {
      queued = false;
      if (!r || ry !== scrollY) { r = el.getBoundingClientRect(); ry = scrollY; }
      const x = ex - r.left, y = ey - r.top;
      el.style.setProperty('--mx', `${x}px`); el.style.setProperty('--my', `${y}px`);
      if (!reduce && fine && tiltTarget) {
        tiltTarget.style.setProperty('--ry', `${((x / r.width) - .5) * 16}deg`);
        tiltTarget.style.setProperty('--rx', `${-((y / r.height) - .5) * 12}deg`);
        tiltTarget.style.setProperty('--s', '1.05');
      }
    };
    el.addEventListener('pointerenter', () => { r = null; });
    el.addEventListener('pointermove', e => { ex = e.clientX; ey = e.clientY; if (!queued) { queued = true; requestAnimationFrame(paint); } });
    el.addEventListener('pointerleave', () => { if (tiltTarget) { tiltTarget.style.setProperty('--rx', '0deg'); tiltTarget.style.setProperty('--ry', '0deg'); tiltTarget.style.setProperty('--s', '1'); } });
  });

  if (!reduce && fine) {
    // 4.9 Soft rubber + neon (owner's pick "O + Q"): letters under the cursor stretch up a little, slowly, like soft
    // rubber, and light up like the orange neon sign from the effects sample (with its quick flicker when switching on). Uses the separate `scale` property (the rise-in owns `transform`), plus colour and
    // text-shadow, so no letter changes width and the words never move to another line.
    if (chars.length) {
      let rects = [];
      const measure = () => { rects = chars.map(c => { const r = c.getBoundingClientRect(); return { c, x: r.left + r.width / 2, y: r.top + r.height / 2 + scrollY, s: 1, vs: 0, g: 0, on: false, at: 0, out: '' }; }); };
      setTimeout(measure, 1400); addEventListener('resize', measure); document.fonts && document.fonts.ready.then(() => setTimeout(measure, 1400));
      idleLoop(() => {
        if (scrollY >= innerHeight) return;
        for (const r of rects) {
          const py = ptr.y - (r.y - scrollY);
          // soft rubber: wide, eased falloff, small stretch, a slow spring with almost no bounce
          let p = Math.max(0, 1 - Math.abs(ptr.x - r.x) / 160) * Math.max(0, 1 - Math.abs(py) / 160); p = p * p * (3 - 2 * p);
          const ts = 1 + .16 * p;
          r.vs = (r.vs + (ts - r.s) * .07) * .8; r.s += r.vs;
          // neon, as in the sample: on within 200px, a 260 ms flicker as it switches on, then a steady glow
          const now = performance.now(), gp = Math.max(0, 1 - Math.hypot(ptr.x - r.x, py) / 200), was = r.on;
          r.on = gp > .15; if (r.on && !was) r.at = now;
          let gv = r.on ? Math.min(1, gp * 1.5) : 0; if (r.on && now - r.at < 260) gv *= Math.random() > .45 ? 1 : .2;
          r.g += (gv - r.g) * .3;
          const sc = Math.abs(r.s - 1) < .001 ? '' : `${(1 / Math.sqrt(r.s)).toFixed(3)} ${r.s.toFixed(3)}`;
          const g = r.g < .01 ? 0 : Math.round(r.g * 100) / 100;
          const out = sc + '|' + g;
          if (out === r.out) continue;
          r.out = out;
          r.c.style.scale = sc;
          r.c.style.color = g ? `color-mix(in oklab, #fff1e4 ${Math.round(g * 100)}%, currentColor)` : '';
          r.c.style.textShadow = g ? `0 0 ${(6 * g).toFixed(1)}px rgba(255,170,110,${g.toFixed(2)}),0 0 ${(22 * g).toFixed(1)}px rgba(255,138,61,${(g * .9).toFixed(2)}),0 0 ${(52 * g).toFixed(1)}px rgba(255,94,26,${(g * .6).toFixed(2)})` : '';
        }
      });
    }
    // Hero product follows the pointer in 3D.
    const photo = band && band.querySelector('.pr-hero-photo');
    const float = band && band.querySelector('[data-vt-float]');
    // the glare is a clone of the photo itself (same classes and rules), lit only where the light hits,
    // so it always lands exactly on the product at any size
    if (photo && float && !float.querySelector('.vt-glare-img')) {
      const glare = photo.cloneNode(false);
      glare.classList.add('vt-glare-img'); glare.alt = ''; glare.setAttribute('aria-hidden', 'true');
      glare.removeAttribute('fetchpriority'); glare.loading = 'lazy';
      photo.after(glare);
    }
    if (photo && float) {
      let hx = 0, hy = 0, hn = 0, box = null;
      // the stage holds the product without its tilt, so its box is measured once (and on resize), never per frame
      const stage = float.parentElement;
      const measureBox = () => { const b = stage.getBoundingClientRect(); box = { left: b.left, top: b.top + scrollY, width: b.width, height: b.height }; };
      addEventListener('resize', () => { box = null; });
      const glareState = {};
      const setOnce = (k, v) => { if (glareState[k] !== v) { glareState[k] = v; float.style.setProperty(k, v); } };
      idleLoop(() => {
        if (scrollY > innerHeight) return;
        if (!box) measureBox();
        // aim at the pointer relative to the product itself (-1..1 across it), like a real object turning to look at you
        const r = { left: box.left, top: box.top - scrollY, width: box.width, height: box.height };
        const ax = Math.max(-1.3, Math.min(1.3, (ptr.x - (r.left + r.width / 2)) / (r.width / 2)));
        const ay = Math.max(-1.3, Math.min(1.3, (ptr.y - (r.top + r.height / 2)) / (r.height / 2)));
        const near = Math.max(0, 1 - Math.hypot(ax, ay) / 2.2);
        hx += (ax - hx) * .12; hy += (ay - hy) * .12; hn += (near - hn) * .1;
        const st = float.style;
        st.setProperty('--vt-ry', `${hx * 24}deg`); st.setProperty('--vt-rx', `${-hy * 18}deg`);
        st.setProperty('--vt-tx', `${hx * 18}px`); st.setProperty('--vt-ty', `${hy * 12 - hn * 10}px`);
        setOnce('--vt-gx', `${Math.round(50 + hx * 45)}%`); setOnce('--vt-gy', `${Math.round(45 + hy * 40)}%`); setOnce('--vt-ga', `${Math.round((.35 + hn * .65) * 50) / 50}`);
        st.setProperty('--vt-sx', `${-hx * 40}px`); st.setProperty('--vt-sk', `${1 - Math.abs(hx) * .2 - hn * .12}`);
        float.style.scale = `${1 + hn * .05}`;
      });
    }
  }

  // Scroll reveals: content is visible at rest; elements below the fold start 56px lower and rise when seen.
  if (!reduce && fine && innerWidth >= 900 && 'IntersectionObserver' in window) {
    const els = document.querySelectorAll('.pr-cat, .vt-spot, .product-card, .pr-trust a');
    const io = new IntersectionObserver(entries => entries.forEach(en => { if (en.isIntersecting) { en.target.classList.remove('vt-pre'); io.unobserve(en.target); } }), { rootMargin: '0px 0px -8% 0px' });
    els.forEach((el, i) => {
      if (el.getBoundingClientRect().top < innerHeight) return;
      el.classList.add('vt-reveal', 'vt-pre'); el.style.transitionDelay = `${(i % 4) * 45}ms`; io.observe(el);
    });
  }

  // WebGL: molten orange light pooled under the hero product, drifting toward the pointer.
  const cv = document.querySelector('[data-vt-gl]');
  if (!cv || !fine || innerWidth < 900) return;
  const gl = cv.getContext('webgl', { antialias: false, powerPreference: 'low-power' });
  if (!gl) return;
  const fs = `precision highp float;uniform vec2 r;uniform float t;uniform vec2 m;
  float h(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
  float n(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);return mix(mix(h(i),h(i+vec2(1,0)),f.x),mix(h(i+vec2(0,1)),h(i+vec2(1,1)),f.x),f.y);}
  float fbm(vec2 p){float v=0.,a=.5;for(int i=0;i<4;i++){v+=a*n(p);p=p*2.03+vec2(1.7,9.2);a*=.5;}return v;}
  void main(){vec2 uv=gl_FragCoord.xy/r;vec2 q=(gl_FragCoord.xy-.5*r)/r.y;float tt=t*.06;
    vec2 w=vec2(fbm(q*1.6+tt),fbm(q*1.6-tt+4.3));float f=fbm(q*2.2+w*1.8+vec2(tt*2.,-tt));
    vec2 c=vec2(mix(.18,.32,m.x)*r.x/r.y,-.1+(.5-m.y)*.15);if(r.x<900.){c=vec2(0.,-.28);}
    float d=length((q-c)*vec2(.9,1.6));float pool=exp(-d*d*5.5);
    vec3 col=vec3(.055,.059,.075)+vec3(.035,.038,.05)*f;
    col+=vec3(1.,.54,.24)*smoothstep(0.,.9,f)*pool*1.15;col+=vec3(1.,.42,.12)*pow(pool,3.)*.35;
    col+=vec3(.9,.6,.4)*pow(max(0.,f-.55),3.)*pool*2.;col*=mix(.6,1.,smoothstep(1.3,.2,length(uv-.5)));
    gl_FragColor=vec4(col,1.);}`;
  const sh = (type, src) => { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); return s; };
  const pr = gl.createProgram();
  gl.attachShader(pr, sh(gl.VERTEX_SHADER, 'attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}'));
  gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, fs)); gl.linkProgram(pr);
  if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) return;
  gl.useProgram(pr);
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(pr, 'p'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  const ur = gl.getUniformLocation(pr, 'r'), ut = gl.getUniformLocation(pr, 't'), um = gl.getUniformLocation(pr, 'm');
  const dpr = .5; // the light is soft: half resolution looks the same and costs a quarter
  const size = () => { cv.width = cv.clientWidth * dpr; cv.height = cv.clientHeight * dpr; gl.viewport(0, 0, cv.width, cv.height); };
  size(); addEventListener('resize', size);
  let visible = true, mx = .7, my = .45;
  new IntersectionObserver(([e]) => { visible = e.isIntersecting; }).observe(cv);
  const t0 = performance.now();
  let last = 0;
  const frame = now => {
    if (visible && !document.hidden && now - last > 32) {
      last = now;
      mx += (ptr.nx - mx) * .03; my += (ptr.ny - my) * .03;
      gl.uniform2f(ur, cv.width / dpr, cv.height / dpr); gl.uniform1f(ut, reduce ? 20 : (now - t0) / 1000); gl.uniform2f(um, mx, my);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
    if (!reduce && (now - t0 < 6000 || now - ptr.at < 2000)) requestAnimationFrame(frame); else glOn = false;
  };
  let glOn = true;
  requestAnimationFrame(frame);
  wakers.push(() => { if (!glOn && !reduce) { glOn = true; requestAnimationFrame(frame); } });
})();

// 4.6 Share sheet: every channel gets the "?view=foco" link (the store with this product in the hero) tagged with its own
// utm_source, so the store analytics show where shared visits come from.
(() => {
  'use strict';
  const tr = s => (globalThis.mcT && globalThis.mcT[s]) || s, trf = (s, v) => tr(s).replace(/\{(\w+)\}/g, (_, k) => v[k]); // i18n: locales via snippets/meucompra-i18n-js.liquid
  const sheet = document.querySelector('[data-share]');
  const opener = document.querySelector('[data-share-open]');
  if (!sheet || !opener) return;
  const base = sheet.dataset.shareUrl, text = sheet.dataset.shareText, title = sheet.dataset.shareTitle;
  const tagged = source => `${base}&utm_source=${source}&utm_medium=compartilhar`;
  const enc = encodeURIComponent;
  const targets = {
    whatsapp: () => `https://wa.me/?text=${enc(`${text}\n${tagged('whatsapp')}`)}`,
    telegram: () => `https://t.me/share/url?url=${enc(tagged('telegram'))}&text=${enc(text)}`,
    facebook: () => `https://www.facebook.com/sharer/sharer.php?u=${enc(tagged('facebook'))}`,
    x: () => `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(tagged('x'))}`,
    email: () => `mailto:?subject=${enc(title)}&body=${enc(`${text}\n${tagged('email')}`)}`,
  };
  sheet.querySelectorAll('[data-share-to]').forEach(a => { a.href = targets[a.dataset.shareTo](); });
  const native = sheet.querySelector('[data-share-native]');
  if (navigator.share) {
    native.hidden = false;
    native.addEventListener('click', () => navigator.share({ title, text, url: tagged('nativo') }).catch(() => {}));
  }
  const copyBtn = sheet.querySelector('[data-share-copy]'), field = sheet.querySelector('#vt-share-url');
  field.value = tagged('link');
  copyBtn.addEventListener('click', async () => {
    try { await navigator.clipboard.writeText(field.value); } catch { field.select(); document.execCommand && document.execCommand('copy'); }
    copyBtn.textContent = tr('Link copiado'); copyBtn.classList.add('is-done');
    setTimeout(() => { copyBtn.textContent = tr('Copiar link'); copyBtn.classList.remove('is-done'); }, 2200);
  });
  const open = () => { sheet.hidden = false; document.documentElement.style.overflow = 'hidden'; sheet.querySelector('.vt-share-x').focus(); };
  const close = () => { sheet.hidden = true; document.documentElement.style.overflow = ''; opener.focus(); };
  opener.addEventListener('click', open);
  sheet.querySelectorAll('[data-share-close]').forEach(el => el.addEventListener('click', close));
  sheet.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
})();

// 4.8 Magnetic grid: a quiet field of dots over the hero light; near the pointer the dots grow, glow orange and part
// like iron filings. Desktop with a mouse only; drawn once at rest and only redrawn while the pointer moves.
(() => {
  'use strict';
  const cv = document.querySelector('[data-vt-trail]');
  const band = document.querySelector('[data-vt-band]');
  if (!cv || !band || innerWidth < 900 || matchMedia('(prefers-reduced-motion: reduce)').matches || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  const ctx = cv.getContext('2d');
  const dpr = Math.min(devicePixelRatio || 1, 1.5), GAP = 30, R = 160;
  let w = 0, h = 0, dots = [];
  const size = () => {
    const r = band.getBoundingClientRect(); w = r.width; h = r.height;
    cv.width = w * dpr; cv.height = h * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    dots = []; for (let y = GAP / 2; y < h; y += GAP) for (let x = GAP / 2; x < w; x += GAP) dots.push(x, y);
    draw();
  };
  let mx = -999, my = -999, tx = -999, ty = -999, pow = 0, tpow = 0, running = false;
  function draw() {
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = 'rgba(255,240,228,.09)';
    ctx.beginPath();
    for (let i = 0; i < dots.length; i += 2) {
      const x = dots[i], y = dots[i + 1], dx = x - mx, dy = y - my;
      if (pow > .01 && dx * dx + dy * dy < R * R) continue;
      ctx.moveTo(x + 1.1, y); ctx.arc(x, y, 1.1, 0, 6.283);
    }
    ctx.fill();
    if (pow <= .01) return;
    for (let i = 0; i < dots.length; i += 2) {
      const x = dots[i], y = dots[i + 1], dx = x - mx, dy = y - my, d2 = dx * dx + dy * dy;
      if (d2 >= R * R) continue;
      const d = Math.sqrt(d2), t = (1 - d / R) * pow, push = t * 14, ox = d ? dx / d * push : 0, oy = d ? dy / d * push : 0;
      ctx.fillStyle = `rgba(255,${Math.round(150 - t * 30)},${Math.round(90 - t * 40)},${.22 + t * .78})`;
      ctx.beginPath(); ctx.arc(x + ox, y + oy, 1.1 + t * 3.2, 0, 6.283); ctx.fill();
    }
  }
  const tick = () => {
    mx += (tx - mx) * .2; my += (ty - my) * .2; pow += (tpow - pow) * .1; draw();
    if (Math.abs(tpow - pow) > .01 || Math.hypot(tx - mx, ty - my) > .4) requestAnimationFrame(tick); else running = false;
  };
  const go = () => { if (!running) { running = true; requestAnimationFrame(tick); } };
  band.addEventListener('pointermove', e => { const r = band.getBoundingClientRect(); tx = e.clientX - r.left; ty = e.clientY - r.top; if (mx < -900) { mx = tx; my = ty; } tpow = 1; go(); });
  band.addEventListener('pointerleave', () => { tpow = 0; go(); });
  size(); addEventListener('resize', size);
})();

// 4.8c: pause looping CSS animations while their section is off screen (marquee, floating product, focus mist).
(() => {
  'use strict';
  if (!('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver(entries => entries.forEach(e => e.target.classList.toggle('vt-off', !e.isIntersecting)));
  document.querySelectorAll('.vt-marquee, [data-vt-band], .vt-spot').forEach(el => io.observe(el));
})();

// 4.9 Footer ("meucompra.shop": solid name, outlined ".shop" that the light fills, ember dot): the wordmark is sized to fill the page width exactly, its letters rise in when the footer is reached, and a
// molten light follows the pointer across it (drifting slowly on its own when nobody points, and resting when off screen).
(() => {
  'use strict';
  const wrap = document.querySelector('[data-vt-giant]');
  if (!wrap) return;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const giant = wrap.querySelector('.vt-giant'), lit = wrap.querySelector('.vt-giant-lit');
  const split = el => { el.innerHTML = [...'meucompra.shop'].map((c, i) => `<span class="vt-g${c === '.' ? ' vt-g-comma' : i > 9 ? ' vt-g-shop' : ''}" style="--i:${i}">${c}</span>`).join(''); };
  split(wrap.querySelector('.vt-giant-base')); split(lit);
  const fit = () => { giant.style.fontSize = '100px'; const w = giant.getBoundingClientRect().width; if (w) giant.style.fontSize = `${(100 * wrap.clientWidth / w) * .995}px`; };
  fit(); addEventListener('resize', fit); document.fonts && document.fonts.ready.then(fit);
  // the display font can arrive after the first fit, so refit whenever the wordmark stops matching the page width
  if ('ResizeObserver' in window) new ResizeObserver(() => { if (Math.abs(giant.getBoundingClientRect().width - wrap.clientWidth * .995) > 2) fit(); }).observe(giant);
  let visible = false, running = false, gx = 62, gy = 55, tx = 62, ty = 55, pointerAt = -1e9;
  const tick = now => {
    if (now - pointerAt > 2200) { const t = now / 1000; tx = 50 + 42 * Math.sin(t * .32); ty = 50 + 22 * Math.sin(t * .57 + 1); }
    gx += (tx - gx) * .08; gy += (ty - gy) * .08;
    lit.style.setProperty('--gx', `${gx.toFixed(1)}%`); lit.style.setProperty('--gy', `${gy.toFixed(1)}%`);
    if (visible) requestAnimationFrame(tick); else running = false;
  };
  const go = () => { if (!running && visible && !reduce) { running = true; requestAnimationFrame(tick); } };
  (wrap.closest('[data-vt-foot]') || wrap).addEventListener('pointermove', e => {
    const r = giant.getBoundingClientRect();
    tx = (e.clientX - r.left) / r.width * 100; ty = (e.clientY - r.top) / r.height * 100; pointerAt = performance.now(); go();
  }, { passive: true });
  if ('IntersectionObserver' in window) new IntersectionObserver(([e]) => { visible = e.isIntersecting; if (visible) { giant.classList.add('is-in'); go(); } }, { threshold: .25 }).observe(wrap);
  else giant.classList.add('is-in');

  // Back to top: the ring leans toward the pointer, and the page glides up.
  const top = document.querySelector('[data-vt-top]');
  if (!top) return;
  const ring = top.querySelector('.vt-top-ring');
  top.addEventListener('click', e => { e.preventDefault(); scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' }); });
  if (!reduce && matchMedia('(hover: hover) and (pointer: fine)').matches) {
    top.addEventListener('pointermove', e => { const r = ring.getBoundingClientRect(); ring.style.setProperty('--tx', `${((e.clientX - r.left - r.width / 2) * .3).toFixed(1)}px`); ring.style.setProperty('--ty', `${((e.clientY - r.top - r.height / 2) * .3).toFixed(1)}px`); });
    top.addEventListener('pointerleave', () => { ring.style.setProperty('--tx', '0px'); ring.style.setProperty('--ty', '0px'); });
  }
})();
